import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useRef, useState, type JSX } from 'react';
import { useAppSelector } from '@/store/hooks/redux';
import EmptyImageCard from '../EmptyImageCard';
import { IExtendedPreviewPropsModel } from '@/editor/shared/components/forms/creatives/models/basic-preview-props.model';
import { Divider } from '@mui/material';
import { useRegularTemplateData } from '@/shared/hooks/useTemplateData';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreativeTypesEnum from '@/editor/shared/constants/creatives-types.enum';

const EmeraEiendomsmeglingBidtheatrePreview: FC<IExtendedPreviewPropsModel> = ({
  caption,
  description,
  areaInUsage,
  viewDate,
  employeeName,
  employeeAvatar,
  employeeEmail,
  employeePhone,
  selectedType,
}): JSX.Element => {
  const singleMedia = useAppSelector(
    (state) => state.creativeStepSliceReducer?.creativeFormsState?.bidtheatre?.single?.media
  );

  const dynamicMedia = useAppSelector(
    (state) => state.creativeStepSliceReducer?.creativeFormsState?.bidtheatre?.dynamic?.creativeMedias
  );
  const bidtheatreShareLink = useAppSelector((state) => state.propertyInfoSliceReducer.bidtheatreShareLink);

  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const totalSlides = dynamicMedia.length;

  useEffect(() => {
    if (selectedType === CreativeTypesEnum.DYNAMIC) {
      startAutoSlide();
      return stopAutoSlide;
    }
  }, [selectedType]);

  const startAutoSlide = () => {
    intervalRef.current = setInterval(() => {
      handleNext();
    }, 4000);
  };

  const stopAutoSlide = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const handleNext = () => {
    if (index === totalSlides) return;
    setIsTransitioning(true);
    setIndex((prev) => prev + 1);
  };

  useEffect(() => {
    if (index === totalSlides) {
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setIndex(0);
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [index, totalSlides]);

  const { t } = useTranslation();

  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (employeeAvatar instanceof File) {
      const url = URL.createObjectURL(employeeAvatar);
      setAvatarUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setAvatarUrl(employeeAvatar);
    }
  }, [employeeAvatar]);

  const isRegularTemplateData = useRegularTemplateData();

  return (
    <div>
      <Grid container sx={{ overflow: 'hidden', position: 'relative' }}>
        {selectedType === CreativeTypesEnum.SINGLE || selectedType === CreativeTypesEnum.CAROUSEL || selectedType == CreativeTypesEnum.VIDEO ? (
          singleMedia?.preSignedUrl ? (
            <Grid
              item
              component="a"
              target="_blank"
              href={bidtheatreShareLink}
              xs={12}
              height={256}
              sx={{
                backgroundImage: `url(${singleMedia?.preSignedUrl})`,
                textDecoration: 'none',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              }}></Grid>
          ) : (
            <EmptyImageCard></EmptyImageCard>
          )) : dynamicMedia?.[0]?.preSignedUrl ? (
            <Grid
              item
              component="a"
              target="_blank"
              href={bidtheatreShareLink}
              xs={12}
              className="eie-preview-box">
              <Box
                sx={{
                  width: '750px',
                  height: '416px',
                  overflow: 'hidden',
                  margin: 'auto',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    width: `${750 * (totalSlides + 1)}px`,
                    transform: `translateX(-${index * 750}px)`,
                    transition: isTransitioning ? 'transform 0.6s ease-in-out' : 'none',
                  }}
                  onTransitionEnd={() => setIsTransitioning(true)}
                >
                  {dynamicMedia.map((src, i) => (
                    <img
                      key={i}
                      src={src.preSignedUrl}
                      alt={`slide-${i}`}
                      style={{ width: '750px', height: '400px', objectFit: 'cover' }}
                    />
                  ))}
                  <img
                    src={dynamicMedia[0].preSignedUrl}
                    alt="cloned-slide"
                    style={{ width: '750px', height: '400px', objectFit: 'cover' }}
                  />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '37%',
                    left: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    color: '#fff',
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: '35px' }} />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '37%',
                    right: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    color: '#fff',
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: '35px' }} />
                </Box>
              </Box>
            </Grid>
          ) : (
          <EmptyImageCard />
        )}
        <Grid item sx={{ position: 'absolute', backgroundColor: '#290F12', padding: '10px 12px', left: '17px' }}>
          <img
            style={{ width: '39px', height: '39px' }}
            src="/images/emera/emera_hovedlogo_beige.svg"
            alt="logo"
            className="logo"
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container sx={{ backgroundColor: '#D7C7B4' }}>
            <Box sx={{ padding: '10px 17px', width: '100%' }}>
              {!isRegularTemplateData && (
                <Typography
                  variant="h6"
                  fontFamily="EMERAThin"
                  sx={{
                    color: '#D7C7B4',
                    bgcolor: '#290F12',
                    width: 'fit-content',
                    textTransform: 'uppercase',
                    padding: '3px 12px',
                  }}>
                  {t('solgt')}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ width: isRegularTemplateData ? '100%' : '70%' }}>
                  <Box>
                    <Typography variant="h6" fontFamily="EMERAThin" sx={{ color: '#290F12', marginTop: '7px' }}>
                      <span>{caption && <div>{caption}</div>}</span>
                    </Typography>
                    <Typography
                      variant="h5"
                      fontFamily="EMERADescription"
                      fontWeight="medium"
                      sx={{ color: '#290F12', margin: '3px 0' }}>
                      {description && description.length > 100 ? description.slice(0, 70) + '...' : description}
                    </Typography>
                  </Box>
                  {isRegularTemplateData && (
                    <Box sx={{ display: 'flex', marginTop: '12px' }}>
                      <Box sx={{ marginRight: '20px' }}>
                        <Typography variant="h6" fontFamily="EMERAThin" sx={{ color: '#290F12' }}>
                          BRA
                        </Typography>
                        <Typography variant="h5" fontFamily="Helvetica" sx={{ color: '#290F12' }}>
                          {areaInUsage}
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem></Divider>
                      <Box sx={{ marginLeft: '20px' }}>
                        <Typography variant="h6" fontFamily="EMERAThin" sx={{ color: '#290F12' }}>
                          VISINING
                        </Typography>
                        <Typography variant="h5" fontFamily="Helvetica" sx={{ color: '#290F12' }}>
                          {viewDate}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {!isRegularTemplateData && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h6" fontFamily="EMERARegular" sx={{ color: '#290F12', fontSize: '13px' }}>
                          {employeeName}
                        </Typography>
                        <Typography variant="h6" fontFamily="EMERARegular" sx={{ color: '#290F12', fontSize: '13px' }}>
                          {employeeEmail}
                        </Typography>
                        <Typography variant="h6" fontFamily="EMERARegular" sx={{ color: '#290F12', fontSize: '13px' }}>
                          {employeePhone}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                {!isRegularTemplateData && (
                  <Grid
                    sx={{
                      backgroundImage: `url(${avatarUrl || undefined})`,
                      textDecoration: 'none',
                      height: '115px',
                      width: '115px',
                      backgroundSize: 'cover',
                      borderRadius: '55%',
                    }}></Grid>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default EmeraEiendomsmeglingBidtheatrePreview;
